import React, { useState, useEffect } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import ReactQuill from "react-quill";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import "react-quill/dist/quill.snow.css";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Switch from "@mui/material/Switch";
import toast, { Toaster } from "react-hot-toast";

//import { sendEmail } from "../../modules/sendMail";
import { sendEmailWithWarmup as sendEmail } from "../../modules/sendMailWithWarmup";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import {
  Container,
  Paper,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { themeColors } from "../../utils/theme";
import { ButtonSkeleton } from "../common/SkeletonLoaders";
import {
  EmailUser,
  EmailRecipient,
  NewsletterFormData,
  UserAPIResponse,
} from "../../types";
import { dnApiConfig } from "../../config/api.config";
import { warmupUserListLambda } from "../../modules/warmupLambda";
import {
  startBatchSend,
  subscribeToBatchProgress,
  fetchBatchProgress,
  BatchProgress,
} from "../../services/emailService";
import { Unsubscribe } from "firebase/firestore";
import { BatchProgressModal } from "./BatchProgressModal";

/**
 * MailerTemplate Component
 *
 * Interface for email composition and reports download.
 * This component supports individual and batch sending, test mode functionality,
 * rich text editing, and S3 file browsing.
 *
 * Features:
 * - Switch between financial reporting and newsletter email types
 * - Rich text editor for email content
 * - Recipient selection with user filtering
 * - Test mode with custom email delivery
 * - Batch sending to all recipients
 * - Month and year selection for financial reporting
 * - S3 file browser for reports download
 *
 * @returns {JSX.Element} Rendered mailer interface
 */
const MailerTemplate: React.FC = () => {
  /** State for form data including email content, recipients, and configuration */
  const [formData, setFormData] = useState<NewsletterFormData>({
    email: "",
    user: null,
    content: "",
    subject: "",
    greeting: "",
    emailList: [],
    sendAll: false,
    testing: false,
    month: "",
    year: "",
    emailType: "financial",
  });

  /** Loading state for form submission */
  const [loading, setLoading] = useState(false);
  /** Loading state for email list fetching */
  const [emailListLoading, setEmailListLoading] = useState(true);
  /** Batch ID for tracking ongoing batch send */
  const [batchId, setBatchId] = useState<string | null>(null);
  /** Real-time progress for current batch */
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(
    null,
  );
  /** Unsubscribe function for batch progress listener */
  const batchProgressUnsubscribeRef = React.useRef<Unsubscribe | null>(null);

  /** Current authenticated user from AuthContext */
  const { currentUser } = useAuth();
  /** Navigation function from react-router */
  const navigate = useNavigate();

  /** Configuration for the ReactQuill rich text editor toolbar */
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: 1 }, { header: 2 }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  /** Available months for reporting period selection */
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  /** Available years for reporting period selection */
  const years = ["2021", "2022", "2023", "2024", "2025", "2026"];

  /**
   * Automatically updates subject and content when month is selected
   *
   * @param {string} month - The selected month
   */
  const month_auto_fill = (month: string) => {
    if (month.length) {
      const message_fill =
        "Please find the attached revenue reporting for " +
        month +
        " " +
        formData.year;
      const subject_fill =
        "Distro Nation " + month + " " + formData.year + " Reporting";

      setFormData({
        ...formData,
        content: message_fill,
        subject: subject_fill,
        month,
      });
    }
  };

  /**
   * Automatically updates subject and content when year is selected
   *
   * @param {string} year - The selected year
   */
  const year_auto_fill = (year: string) => {
    if (year.length) {
      const message_fill =
        "Please find the attached revenue reporting for " +
        formData.month +
        " " +
        year;
      const subject_fill =
        "Distro Nation " + formData.month + " " + year + " Reporting";

      setFormData({
        ...formData,
        content: message_fill,
        subject: subject_fill,
        year,
      });
    }
  };

  /**
   * Fetches user list from API on component mount
   * Initializes email recipient list and handles API errors
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setEmailListLoading(true);

        const userListWarmup = await warmupUserListLambda();
        console.log("userListWarmup", userListWarmup);

        const response = await axios.get<UserAPIResponse>(
          "https://cjed05n28l.execute-api.us-east-1.amazonaws.com/staging/dn_users_list",
          {
            headers: {
              "x-api-key": dnApiConfig.apiKey,
            },
          },
        );
        const exclude = [
          "The Orchard",
          "Mando Chastouki",
          "Victoria Abah",
          "Kelvin Mensah",
          "Welbeck Tawiah",
        ];
        let users = response.data.body;
        users = users.filter((item) => item.status === "ACTIVE");
        //filter out users with emails that are not in the exclude list
        users = users.filter(
          (item) => !exclude.includes(item.userName) && item.emailsUser,
        );
        console.log(users);
        setFormData((prev) => ({ ...prev, emailList: users }));
      } catch (error) {
        console.error(error);
        toast.error("Failed to load recipient list");
      } finally {
        setEmailListLoading(false);
      }
    };
    fetchData();
  }, []);

  /**
   * Cleanup effect: unsubscribe from batch progress listener when component unmounts
   */
  useEffect(() => {
    return () => {
      if (batchProgressUnsubscribeRef.current) {
        batchProgressUnsubscribeRef.current();
      }
    };
  }, []);

  /**
   * Updates selected email recipient and extracts channel information
   *
   * @param {React.SyntheticEvent} _e - The event object (unused)
   * @param {EmailUser | null} value - The selected user object
   */
  const emailRecipientUpdate = (
    _e: React.SyntheticEvent,
    value: EmailUser | null,
  ) => {
    if (value) {
      let channels: any[] = [];
      let email = value.emailsUser;
      if (value.creators && Array.isArray(value.creators)) {
        for (let i = 0; i < value.creators.length; i++) {
          let creator = value.creators[i];
          if (creator.channels && Array.isArray(creator.channels)) {
            for (let j = 0; j < creator.channels.length; j++) {
              channels.push(creator.channels[j]);
            }
          }
        }
      }
      let obj: EmailRecipient = {
        channels: channels,
        email: email,
      };
      if (email) {
        setFormData((prev) => ({
          ...prev,
          email,
          user: obj,
        }));
      }
    }
  };

  /**
   * Handles form submission and email sending
   * Supports individual and batch sending modes
   *
   * @param {React.FormEvent} event - The form submission event
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const {
      email,
      user,
      content,
      greeting,
      subject,
      month,
      year,
      emailList,
      sendAll,
      emailType,
    } = formData;

    if ((!email && !sendAll) || !content || !subject || !greeting) {
      toast.error("All fields are required");
      return;
    }

    setLoading(true);

    if (sendAll) {
      // Use batch send API for sending to all recipients
      try {
        // Call the batch send API endpoint
        const response = await startBatchSend({
          subject,
          message_greeting: greeting,
          message_body: content,
          emailType: emailType as "financial" | "newsletter",
          ...(month && { month }),
          ...(year && { year }),
        });

        // Extract batch ID and total recipients from response
        const newBatchId = response.batchId;
        setBatchId(newBatchId);

        // Initialize batch progress state
        setBatchProgress({
          sent: 0,
          failed: 0,
          total: response.totalRecipients,
          percent: 0,
          status: response.status,
        });

        // Show success toast for batch initiation
        toast.success(
          `Batch send initiated. Processing ${response.totalRecipients} recipients...`,
        );

        // Subscribe to real-time progress updates
        const unsubscribe = subscribeToBatchProgress(
          newBatchId,
          // onUpdate callback: update progress as emails are sent
          (progress: BatchProgress) => {
            setBatchProgress(progress);
          },
          // onComplete callback: show completion summary when batch finishes
          (_batch) => {
            setLoading(false);
            // Show completion summary
            toast.success(
              `Batch complete! Sent: ${_batch.sentCount}, Failed: ${_batch.failedCount}`,
            );
            // Reset batch state
            setBatchId(null);
            setBatchProgress(null);
          },
        );

        // Store unsubscribe function for cleanup
        batchProgressUnsubscribeRef.current = unsubscribe;

        // FALLBACK: Also poll batch status every 2 seconds in case Firestore listener doesn't work
        const pollInterval = setInterval(async () => {
          try {
            const progress = await fetchBatchProgress(newBatchId);

            if (progress) {
              console.log("[Polling] Fetched batch progress:", progress);
              setBatchProgress(progress);

              // Stop polling if complete
              if (progress.status === "completed") {
                clearInterval(pollInterval);
                setLoading(false);
                toast.success(
                  `Batch complete! Sent: ${progress.sent}/${progress.total}, Failed: ${progress.failed}`,
                );
                setBatchId(null);
                setBatchProgress(null);
              }
            }
          } catch (error) {
            console.error("[Polling] Error fetching batch progress:", error);
          }
        }, 2000); // Poll every 2 seconds

        // Clean up polling on unmount
        return () => {
          clearInterval(pollInterval);
          if (batchProgressUnsubscribeRef.current) {
            batchProgressUnsubscribeRef.current();
          }
        };
      } catch (error) {
        setLoading(false);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to initiate batch send";
        toast.error(`Batch send failed: ${errorMessage}`);
        console.error("Batch send error:", error);
      }
    } else {
      // Handle single recipient send
      if (!user) {
        toast.error("Please select a recipient");
        setLoading(false);
        return;
      }
      await toast.promise(
        sendEmail(
          user,
          content,
          greeting,
          subject,
          month,
          year,
          emailType,
          formData.testing,
          formData.email,
        ),
        {
          loading: "Sending email for " + email,
          success: () => `Successfully delivered ` + email,
          error: (err: any) => `This just happened: ${err.toString()}`,
        },
        {
          style: {
            minWidth: "250px",
          },
          success: {
            duration: 5000,
            icon: "🚀",
          },
        },
      );
      setLoading(false);
    }
  };

  /**
   * Renders access denied view for unauthenticated users
   *
   * @returns {JSX.Element} Access denied message with login button
   */
  if (!currentUser) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, mb: 4 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
              Access Denied
            </Typography>
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              You must be logged in to access this page
            </Alert>
            <Button
              variant="contained"
              sx={{ bgcolor: themeColors.primary }}
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  /**
   * Main component render for authenticated users
   *
   * @returns {JSX.Element} Complete mailer interface with Email Templates and Reports sections
   */
  return (
    <Box sx={{ mb: 4 }}>
      <Toaster />
      <BatchProgressModal
        show={loading && batchId !== null}
        batchId={batchId || undefined}
        progress={batchProgress}
        onComplete={(summary) => {
          // Show success toast with batch summary
          toast.success(
            `Batch complete! Sent: ${summary.sent}/${summary.total}, Failed: ${summary.failed}, Time: ${(summary.processingTime / 1000).toFixed(1)}s`,
            {
              duration: 5000,
            },
          );
          // Reset batch state
          setBatchId(null);
          setBatchProgress(null);
          setLoading(false);
          if (batchProgressUnsubscribeRef.current) {
            batchProgressUnsubscribeRef.current();
            batchProgressUnsubscribeRef.current = null;
          }
        }}
        onClose={() => {
          setBatchId(null);
          setBatchProgress(null);
          setLoading(false);
          if (batchProgressUnsubscribeRef.current) {
            batchProgressUnsubscribeRef.current();
            batchProgressUnsubscribeRef.current = null;
          }
        }}
      />
      <Paper
        elevation={3}
        sx={{
          bgcolor: themeColors.paper,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Email Templates Section */}
        <Box
          sx={{
            p: { xs: 2, sm: 2, md: 2 },
            pl: { xs: 2, sm: 3, md: 3 },
            pr: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            minHeight: "70vh",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              color: themeColors.primary,
              fontWeight: 600,
            }}
          >
            Email Templates
          </Typography>
          <Box
            sx={{
              p: 0,
              mb: 4,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight:
                    formData.emailType === "financial" ? "bold" : "normal",
                }}
              >
                Financial Reporting
              </Typography>
              <Switch
                checked={formData.emailType === "newsletter"}
                onChange={(e) => {
                  const newEmailType = e.target.checked
                    ? "newsletter"
                    : "financial";
                  setFormData((prev) => ({
                    ...prev,
                    emailType: newEmailType,
                    // Clear month and year when switching to newsletter
                    ...(newEmailType === "newsletter"
                      ? { month: "", year: "" }
                      : {}),
                  }));
                }}
                color="primary"
                sx={{ mx: 1 }}
              />
              <Typography
                sx={{
                  fontWeight:
                    formData.emailType === "newsletter" ? "bold" : "normal",
                }}
              >
                Newsletter
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: themeColors.primary,
              fontWeight: 500,
              fontSize: "1.1rem",
            }}
          >
            Email Configuration
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3} direction="row" sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  id="subject"
                  label="Subject"
                  variant="outlined"
                  fullWidth
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  sx={{ mb: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  id="greeting"
                  label="Greeting"
                  variant="outlined"
                  fullWidth
                  value={formData.greeting}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      greeting: e.target.value,
                    }))
                  }
                  sx={{ mb: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box>
                  <FormControl fullWidth>
                    <Autocomplete
                      id="email"
                      options={formData.emailList}
                      loading={emailListLoading}
                      getOptionLabel={(option) =>
                        option.userName + " - " + option.emailsUser
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Recipient"
                          variant="outlined"
                          sx={{ mb: 0 }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {emailListLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      onChange={emailRecipientUpdate}
                      disabled={
                        formData.sendAll || formData.testing || emailListLoading
                      }
                    />
                  </FormControl>
                </Box>
              </Grid>
            </Grid>

            {/* Month and Year inputs - only show for financial reporting */}
            {formData.emailType === "financial" && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    mt: 3,
                    color: themeColors.primary,
                    fontWeight: 500,
                    fontSize: "1.1rem",
                  }}
                >
                  Reporting Period
                </Typography>
                <Grid container spacing={3} direction="row" sx={{ mb: 4 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <Autocomplete
                        id="month"
                        options={months}
                        getOptionLabel={(option) => option}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Month"
                            variant="outlined"
                            sx={{ mb: 0 }}
                          />
                        )}
                        onChange={(_e, value) => {
                          if (value) {
                            month_auto_fill(value);
                          }
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <Autocomplete
                        id="year"
                        options={years}
                        getOptionLabel={(option) => option}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Year"
                            variant="outlined"
                            sx={{ mb: 0 }}
                          />
                        )}
                        onChange={(_e, value) => {
                          if (value) {
                            year_auto_fill(value);
                          }
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    {/* <!-- Test Mode and Send to All checkboxes for financial reporting --> */}
                    <FormControl>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          color="primary"
                          checked={formData.testing}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              testing: e.target.checked,
                              email: "",
                              user: prev.user
                                ? { ...prev.user, email: "" }
                                : null, // Preserve channels
                            }));
                          }}
                        />
                        <Typography>Test Mode</Typography>
                      </Box>
                    </FormControl>
                    <FormControl>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          color="primary"
                          checked={formData.sendAll}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              sendAll: e.target.checked,
                              email: "",
                              user: null,
                            }));
                          }}
                          disabled={formData.testing}
                        />
                        <Typography>Send to all</Typography>
                      </Box>
                    </FormControl>
                  </Grid>
                </Grid>
              </>
            )}

            {/* For Newsletter mode, show only Test Mode and Send to All checkboxes */}
            {formData.emailType === "newsletter" && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    mt: 3,
                    color: themeColors.primary,
                    fontWeight: 500,
                    fontSize: "1.1rem",
                  }}
                >
                  Newsletter Options
                </Typography>
                <Grid container spacing={3} direction="row" sx={{ mb: 4 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", gap: 4 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          color="primary"
                          checked={formData.testing}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              testing: e.target.checked,
                              email: "",
                              user: prev.user
                                ? { ...prev.user, email: "" }
                                : null, // Preserve channels
                            }));
                          }}
                          sx={{ p: 1 }}
                        />
                        <Typography
                          sx={{ color: themeColors.text, fontWeight: 500 }}
                        >
                          Test Mode
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          color="primary"
                          checked={formData.sendAll}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              sendAll: e.target.checked,
                              email: "",
                              user: null,
                            }));
                          }}
                          disabled={formData.testing}
                          sx={{ p: 1 }}
                        />
                        <Typography
                          sx={{ color: themeColors.text, fontWeight: 500 }}
                        >
                          Send to all
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}></Grid>
                </Grid>
              </>
            )}

            {formData.testing && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    mt: 3,
                    color: themeColors.primary,
                    fontWeight: 500,
                    fontSize: "1.1rem",
                  }}
                >
                  Test Configuration
                </Typography>
                <Grid container spacing={3} direction="row" sx={{ mb: 4 }}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      id="email"
                      type="email"
                      label="Test Email Address"
                      variant="outlined"
                      fullWidth
                      value={formData.email}
                      onChange={(e) => {
                        const email = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          email,
                          user: { ...prev.user, email } as EmailRecipient,
                        }));
                      }}
                      error={
                        formData.email === "" ||
                        !formData.email.includes("@") ||
                        !formData.email.includes(".") ||
                        (!formData.email.includes(".com") &&
                          !formData.email.includes(".net") &&
                          !formData.email.includes(".org") &&
                          !formData.email.includes(".co"))
                      }
                      sx={{ mb: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}></Grid>
                </Grid>
              </>
            )}

            <Typography
              variant="h6"
              sx={{
                mb: 2,
                mt: 3,
                color: themeColors.primary,
                fontWeight: 500,
                fontSize: "1.1rem",
              }}
            >
              Email Content
            </Typography>
            <Box
              sx={{
                mb: 4,
                "& .ql-container": {
                  minHeight: "300px",
                  fontSize: "14px",
                },
                "& .ql-editor": {
                  minHeight: "280px",
                  lineHeight: 1.6,
                },
                "& .ql-toolbar": {
                  border: "1px solid #ccc",
                  borderTopLeftRadius: "8px",
                  borderTopRightRadius: "8px",
                  "& .ql-stroke": {
                    stroke: themeColors.text + " !important",
                  },
                  "& .ql-fill": {
                    fill: themeColors.text + " !important",
                  },
                  "& .ql-picker-label": {
                    color: themeColors.text + " !important",
                  },
                  "& button": {
                    color: themeColors.text + " !important",
                    "&:hover": {
                      color: themeColors.primary + " !important",
                    },
                  },
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(content) =>
                  setFormData((prev) => ({ ...prev, content }))
                }
                modules={modules}
                style={{ height: "300px", borderRadius: "8px" }}
              />
            </Box>

            <Box
              sx={{
                mt: 6,
                pt: 3,
                borderTop: `1px solid ${themeColors.accent}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: themeColors.textSecondary }}
              >
                {formData.sendAll && !formData.testing
                  ? `Ready to send to ${formData.emailList.length} recipients`
                  : formData.testing
                    ? "Test mode enabled"
                    : "Single recipient mode"}
              </Typography>
              {loading ? (
                <ButtonSkeleton width={140} height={48} />
              ) : (
                <Button
                  variant="contained"
                  type="submit"
                  size="large"
                  sx={{
                    bgcolor: themeColors.primary,
                    color: "black",
                    px: 5,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: `${themeColors.primary}cc`,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px ${themeColors.primary}40`,
                    },
                    "&:disabled": {
                      bgcolor: themeColors.accent,
                      color: themeColors.textSecondary,
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                  disabled={
                    (!formData.email && !formData.sendAll) ||
                    !formData.content ||
                    !formData.subject ||
                    !formData.greeting
                  }
                >
                  {formData.testing ? "Send Test" : "Send Email"}
                </Button>
              )}
            </Box>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default MailerTemplate;
